import {
  Card,
  CardHeader,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import type {SxProps, Theme} from "@mui/material";
import {translate} from "@util";
import dayjs from "dayjs";
import {useSelector} from "react-redux";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {RestoreOutlined} from "@mui/icons-material";
import {EditIcon} from "@components/icons";
import {Selectors} from "@core/store/selectors";
import {format} from "@constants";
import {UserAvatar} from "@components/UserAvatar";
import {User} from "@core/model/User";

const Profile = ({
  sx,
  minimize = false,
  user,
  onEdit,
}: {
  sx?: SxProps<Theme>;
  minimize?: boolean;
  user: User | undefined;
  onEdit: () => void;
}) => {
  const userRole = useSelector(Selectors.userRole);

  if (minimize) {
    return (
      <>
        <MenuItem
          onClick={onEdit}
          sx={{
            minHeight: "56px !important",
            borderBottom: t => `1px solid ${t.palette.divider}`,
          }}>
          <ListItemAvatar>
            {user && <UserAvatar width={40} id={user?.id} />}
          </ListItemAvatar>
          <ListItemText
            primary={`${user?.name} ${user?.surname}`}
            secondary={user?.email}
          />
        </MenuItem>
      </>
    );
  }

  return (
    <Card
      onClick={e => e.stopPropagation()}
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...sx,
      }}>
      <CardHeader
        title={translate("pages.home.profile.title")}
        action={
          <IconButton size="small" onClick={onEdit} disabled={!user}>
            <EditIcon fontSize="inherit" />
          </IconButton>
        }
      />
      <Stack
        flexGrow={1}
        px={2}
        pb={1}
        gap={2}
        direction="row"
        alignItems="center">
        {user ? (
          <>
            <UserAvatar width={60} id={user.id} variant="h6" />
            <Stack>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                lineHeight={1.3}>
                {user.name} {user.surname}
              </Typography>
              {user.email && (
                <Typography variant="body1">{user.email}</Typography>
              )}
              {user.phone && (
                <Typography variant="body1">{user.phone}</Typography>
              )}
              {userRole && (
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="secondary"
                  lineHeight={1.3}>
                  {translate(`common.role.${userRole}`)}
                </Typography>
              )}
            </Stack>
          </>
        ) : (
          <Stack direction={"row"} gap={2} alignItems={"center"}>
            <Skeleton variant="circular" width={64} height={64} />
            <Stack gap={1}>
              <Skeleton variant="rectangular" width={130} height={16} />
              <Skeleton variant="rectangular" width={130} height={8} />
            </Stack>
          </Stack>
        )}
      </Stack>
      <Divider />
      <Stack p={1} direction="row" gap={1} justifyContent="space-around">
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          gap={1}>
          <CalendarMonthIcon fontSize="large" color="primary" />
          <Stack>
            <Typography variant="caption" color="secondary">
              {translate("pages.home.profile.today")}
            </Typography>
            <Typography
              variant="caption"
              color="textPrimary"
              textTransform={"capitalize"}>
              {dayjs().format(format.date.short)}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          width={140}
          gap={1}>
          <RestoreOutlined fontSize="large" color="primary" />
          <Stack>
            <Typography variant="caption" color="secondary">
              {translate("pages.home.profile.lastLogin")}
            </Typography>
            <Typography variant="caption" color="textPrimary">
              {user ? dayjs(user.lastLogin).format(format.datetime.short) : "-"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

export {Profile};
